import React, { useState, useEffect, useMemo } from "react";
import {
  Search,
  Shield,
  Settings,
  Map,
  Car,
  DollarSign,
  BarChart3,
  UserPlus,
  Gift,
  Handshake,
  MessageSquare,
  FileText,
  Clock,
  AlertCircle,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Permission } from "@/types/auth";
import DataTablePagination from "@/components/ui/data-table-pagination";
import { MOCK_PERMISSIONS } from "@/lib/permissions";

const PermissionsPage: React.FC = () => {
  const [permissions] = useState<Permission[]>(MOCK_PERMISSIONS);
  const [filteredPermissions, setFilteredPermissions] = useState<Permission[]>(MOCK_PERMISSIONS);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [resourceFilter, setResourceFilter] = useState<string>("all");

  // Ref to maintain focus on search input
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Only search if term is more than 2 characters (3+)
  const shouldSearch = debouncedSearchTerm.trim().length > 2;

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Filter permissions based on search and filters
  useEffect(() => {
    let filtered = permissions;

    if (shouldSearch) {
      filtered = filtered.filter(
        (permission) =>
          permission.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
          permission.description?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
          permission.resource.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
          permission.action.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
          (permission.category && permission.category.toLowerCase().includes(debouncedSearchTerm.toLowerCase()))
      );
    }

    if (categoryFilter !== "all") {
      filtered = filtered.filter((permission) => permission.category === categoryFilter);
    }

    if (resourceFilter !== "all") {
      filtered = filtered.filter((permission) => permission.resource === resourceFilter);
    }

    setFilteredPermissions(filtered);
    setCurrentPage(1);
  }, [permissions, debouncedSearchTerm, categoryFilter, resourceFilter, shouldSearch]);

  // Auto-focus search input on page load
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Pagination calculations
  const totalItems = filteredPermissions.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredPermissions.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => setCurrentPage(page);
  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const getResourceIcon = (resource: string) => {
    switch (resource) {
      case "dashboard":
        return <BarChart3 className="h-4 w-4" />;
      case "users":
      case "roles":
      case "permissions":
        return <UserPlus className="h-4 w-4" />;
      case "geography":
        return <Map className="h-4 w-4" />;
      case "drivers":
        return <Car className="h-4 w-4" />;
      case "riders":
        return <UserPlus className="h-4 w-4" />;
      case "vehicles":
      case "pricing":
      case "surge":
        return <DollarSign className="h-4 w-4" />;
      case "promotions":
        return <Gift className="h-4 w-4" />;
      case "referrals":
        return <Handshake className="h-4 w-4" />;
      case "disputes":
        return <MessageSquare className="h-4 w-4" />;
      case "reports":
        return <FileText className="h-4 w-4" />;
      case "audit":
        return <AlertCircle className="h-4 w-4" />;
      case "settings":
        return <Clock className="h-4 w-4" />;
      case "security":
        return <Shield className="h-4 w-4" />;
      default:
        return <Settings className="h-4 w-4" />;
    }
  };

  const getActionBadge = (action: string) => {
    switch (action) {
      case "view":
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800">View</Badge>;
      case "create":
        return <Badge variant="default" className="bg-green-100 text-green-800">Create</Badge>;
      case "edit":
        return <Badge variant="outline" className="bg-yellow-100 text-yellow-800">Edit</Badge>;
      case "delete":
        return <Badge variant="destructive" className="bg-red-100 text-red-800">Delete</Badge>;
      case "activate":
        return <Badge className="bg-purple-100 text-purple-800">Activate</Badge>;
      case "manage":
        return <Badge className="bg-indigo-100 text-indigo-800">Manage</Badge>;
      default:
        return <Badge variant="outline">{action}</Badge>;
    }
  };

  const categories = useMemo(() =>
    Array.from(new Set(permissions.map((p) => p.category || "Other"))).filter(Boolean),
    [permissions]
  );

  const resources = useMemo(() =>
    Array.from(new Set(permissions.map((p) => p.resource))),
    [permissions]
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Permissions</h1>
          <p className="text-muted-foreground">
            Role-Based Access Control (RBAC) permissions for the application
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Permissions
            </CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{permissions.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Categories
            </CardTitle>
            <Settings className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{categories.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Resources
            </CardTitle>
            <FileText className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{resources.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              User Management
            </CardTitle>
            <UserPlus className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {permissions.filter((p) => p.category === "User Management").length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                type="search"
                placeholder="Search permissions by name, description, resource, or action..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
                autoComplete="off"
                className="pl-10"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={resourceFilter} onValueChange={setResourceFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by resource" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Resources</SelectItem>
                {resources.map((resource) => (
                  <SelectItem key={resource} value={resource}>
                    {resource.charAt(0).toUpperCase() + resource.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Permissions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Permissions ({filteredPermissions.length})</CardTitle>
          <CardDescription>
            System permissions organized by category and resource. These are used to control access to various features.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Permission</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Resource</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Description</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedData.map((permission) => (
                <TableRow key={permission.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
                        <Shield className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <div className="font-medium">{permission.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {permission.id}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="border-primary/20">
                      {permission.category || "Other"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getResourceIcon(permission.resource)}
                      <span className="capitalize">{permission.resource}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {getActionBadge(permission.action)}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-muted-foreground max-w-[300px]">
                      {permission.description}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {filteredPermissions.length === 0 ? (
            <div className="text-center py-8">
              <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium">No permissions found</h3>
              <p className="text-muted-foreground">
                {searchTerm || categoryFilter !== "all" || resourceFilter !== "all"
                  ? "Try adjusting your filters"
                  : "No permissions available"}
              </p>
            </div>
          ) : (
            <div className="mt-4">
              <DataTablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                className="mt-4"
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PermissionsPage;
